import { FormControl, Grid, Radio, RadioGroup, TextField } from '@material-ui/core';
import { Button, Card, Icons, Typography } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useMst } from '../../mobx-models/Root';
import { Deficiency, IPmPayload } from '../../models/models';
import { PmCheckList, PmCheckListChildren } from '../../rxdb/pmCheckList/queryBuilder';
import usePmCheckList from '../../rxdb/pmCheckList/usePmCheckList';
import { SavePmChecklistDocument } from '../../rxdb/savepmChecklist/queryBuilder';
import useSavePmChecklist from '../../rxdb/savepmChecklist/useSavePmChecklist';
import useAccessOnTask from '../../rxdb/taskList/useAccessOnTask';
import { useTaskList } from '../../rxdb/taskList/useTaskList';
import { sentryCaptureException, useNotification } from '../../utilities';
import { PMheader, PMsubtitle } from '../../utilities/constants';
import { TaskStatus } from '../../utilities/enums';
import { useWindowDimensions } from '../../utilities/useWindowDimensions';
import { isNull, isTaskStatusFIorLISign } from '../../utilities/utilityFunctions';
import EditDeficiency from '../EditDeficiency';
import useStyles from './PMChecklist.styles';

const PMChecklist: React.FC = () => {
  const classes = useStyles();
  const { SavePMChecklist, CurrentUserSelected, SiteDiscipline } = useMst();
  const { isEditable, taskData } = useAccessOnTask();
  const size = useWindowDimensions();
  const { successNotification } = useNotification();
  const { setTaskListInfo } = useTaskList();
  const { pmCheckListLookUpData } = usePmCheckList(SiteDiscipline.siteId);
  const { pmChecklistTransactionData, savePmChecklistTransactionData } = useSavePmChecklist();

  const [editDeficiencyDialog, setEditDeficiencyDialog] = useState<boolean>(false);
  const [editFile, setEditFile] = useState<Deficiency>();
  const [addFlag, setAddFlag] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [PMChecklistPayload, setPMChecklistPayload] = useState<IPmPayload[]>([]);

  const isAdhoc = CurrentUserSelected.isAdhoc;

  useEffect(() => {
    const getData = (val: {
      assetCategoryInspectionTypeCheckListId: number;
      stepNo: number;
      stepName: string;
      stepDetails: string;
      children?: PmCheckListChildren;
      id: string;
      parentId: number;
    }) => {
      const index = pmChecklistTransactionData?.findIndex(
        (res: SavePmChecklistDocument) =>
          res?.assetCategoryInspectionTypeCheckListId ===
          val?.assetCategoryInspectionTypeCheckListId,
      );
      if (index !== -1) {
        return {
          assetCategoryInspectionTypeCheckListId: val.assetCategoryInspectionTypeCheckListId,
          pk_uuid: pmChecklistTransactionData[index]?.pk_uuid,
          isCompleted: pmChecklistTransactionData[index]?.isCompleted,
          comments: pmChecklistTransactionData[index]?.comments,
          isCompletedChange: false,
          isCommentChange: false,
          pkAssetInspectionPMChecklist:
            pmChecklistTransactionData[index]?.pkAssetInspectionPMChecklist,
          parentId: val.parentId,
        };
      } else
        return {
          assetCategoryInspectionTypeCheckListId: val.assetCategoryInspectionTypeCheckListId,
          comments: '',
          isCompletedChange: false,
          isCommentChange: false,
          isCompleted: null,
          parentId: val.parentId,
        };
    };
    const data = pmCheckListLookUpData?.map((val: PmCheckList) => {
      const result = getData({ ...JSON.parse(JSON.stringify(val)), parentId: 0 });
      const res1 = [{ ...result }];
      if (val.children && val.children?.length) {
        val.children?.map((val2: PmCheckListChildren) => {
          res1.push(getData({ ...val2, parentId: val.assetCategoryInspectionTypeCheckListId }));
        });
      }
      return res1;
    });
    if (data?.length > 0) {
      data.forEach(element => {
        if (element?.length > 1) {
          element.forEach((i, index) => i.parentId === 0 && element.splice(index, 1));
        }
      });
    }
    setPMChecklistPayload(data.flat() as IPmPayload[]);
    SavePMChecklist.setDiscardData(data.flat() as IPmPayload[]);
  }, [SavePMChecklist, pmCheckListLookUpData, pmChecklistTransactionData]);

  const CheckListUI = (props: {
    stepDetails: string;
    assetCategoryInspectionTypeCheckListId: number;
    stepNo: number;
    index: number;
    isChild: boolean;
  }) => {
    const [comment] = useState(
      SavePMChecklist?.PMcheckListData?.find(
        val =>
          props.assetCategoryInspectionTypeCheckListId ===
          val.assetCategoryInspectionTypeCheckListId,
      ),
    );

    const [isCompleted] = useState(
      SavePMChecklist?.PMcheckListData?.find(
        val =>
          props.assetCategoryInspectionTypeCheckListId ===
          val.assetCategoryInspectionTypeCheckListId,
      ),
    );

    const handleChange = (value: boolean) => {
      SavePMChecklist.setIsChanged(true);
      let index: number;
      index = PMChecklistPayload.findIndex(
        (val: IPmPayload) =>
          props.assetCategoryInspectionTypeCheckListId ===
          val.assetCategoryInspectionTypeCheckListId,
      );
      PMChecklistPayload[index] = {
        ...PMChecklistPayload[index],
        isCompleted: value,
        isCompletedChange: true,
      };
      if (PMChecklistPayload[index]?.parentId) {
        index = PMChecklistPayload.findIndex(
          (val: IPmPayload) =>
            PMChecklistPayload[index]?.parentId === val.assetCategoryInspectionTypeCheckListId,
        );
        PMChecklistPayload[index] = {
          ...PMChecklistPayload[index],
          isCompleted: false,
          isCompletedChange: true,
        };
      }
      SavePMChecklist.setDiscardData(PMChecklistPayload);
    };

    const onCommentInput = (event: React.FocusEvent<HTMLInputElement>) => {
      SavePMChecklist.setIsChanged(true);
      const { value } = event.target;
      const index = PMChecklistPayload.findIndex(
        (val: IPmPayload) =>
          props.assetCategoryInspectionTypeCheckListId ===
          val.assetCategoryInspectionTypeCheckListId,
      );
      PMChecklistPayload[index] = {
        ...PMChecklistPayload[index],
        comments: value,
        isCommentChange: true,
      };
      SavePMChecklist.setDiscardData(PMChecklistPayload);
    };

    return (
      <Grid item className={!props.isChild ? classes.grid : classes.gridSubStep} xs={12} container>
        {!props.isChild && (
          <Grid item xs={1} className={classes.stepGrid} direction="row">
            <Typography className={size.height > size.width ? 'stepVertical' : 'step'}>
              {`Step ${props.stepNo}`}
            </Typography>
          </Grid>
        )}
        {props.isChild && <Grid item xs={1} />}
        <Grid
          data-testid={`step-details${props.stepNo}`}
          item
          xs={size.height > size.width ? 7 : 6}
        >
          <Typography>
            <pre className={classes.preTag}> {props.stepDetails.trim()}</pre>
            <br />
          </Typography>
          <div className={classes.radio}>
            <FormControl>
              <RadioGroup
                key={props.assetCategoryInspectionTypeCheckListId}
                defaultValue={
                  isCompleted?.isCompleted === true
                    ? 'isCompleted'
                    : isCompleted?.isCompleted === false
                    ? 'notApplicable'
                    : undefined
                }
              >
                <div className={classes.radioStyle}>
                  <Radio
                    data-testid={`select-option-completed${props.stepNo}`}
                    color="primary"
                    value="isCompleted"
                    onChange={() => handleChange(true)}
                    disabled={!isEditable}
                  />
                  <Typography className="radioComplete radioCompleteMarginRight">
                    Completed
                  </Typography>
                  <Radio
                    data-testid={`select-option-notA${props.stepNo}`}
                    color="primary"
                    value="notApplicable"
                    onChange={() => handleChange(false)}
                    disabled={!isEditable}
                  />
                  <Typography className="radioComplete">Not Applicable</Typography>
                </div>
              </RadioGroup>
            </FormControl>
          </div>
        </Grid>

        <Grid
          item
          xs={size.height > size.width ? 4 : 5}
          className={!props.isChild ? classes.commentGrid : classes.commentSubStep}
        >
          <Typography color="primary" className={classes.comment}>
            Comment
          </Typography>
          <TextField
            key={props.assetCategoryInspectionTypeCheckListId}
            variant="outlined"
            size="small"
            placeholder="Input text"
            multiline
            rows={4}
            rowsMax={4}
            className={classes.textField}
            onChange={onCommentInput}
            defaultValue={comment?.comments || ''}
            disabled={!isEditable}
            inputProps={{
              'data-testid': 'commentsField',
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const saveData = async () => {
    try {
      setIsSaving(true);
      if (isTaskStatusFIorLISign(taskData) || isAdhoc) {
        await setTaskListInfo(TaskStatus.IN_PROGRESS);
      }
      PMChecklistPayload?.map(async (record: IPmPayload) => {
        if (record?.isCompletedChange || record?.isCommentChange) {
          await savePmChecklistTransactionData({
            ...record,
            pk_uuid: isNull(record, 'pk_uuid', uuidv4()),
            inspectionId: CurrentUserSelected.inspectionId,
            fkAssetInspectionTask: CurrentUserSelected.taskId,
            isActive: record.isActive as boolean,
          });
        }
      });
      setIsSaving(false);
      SavePMChecklist.setIsChanged(false);
      successNotification('PM Checklist Saved Successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('🚀 ~ file: PMChecklist.tsx ~ line 298 ~ saveData ~ err', error);
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, `~ file: PMChecklist.tsx ~ line 298 ~ saveData ${error}`);
    }
  };

  return (
    <div className={classes.root}>
      <Card>
        <Grid item className={classes.card} xs={12} container>
          <Grid
            item
            className={classes.PMheaderGrid}
            xs={size.height > size.width ? 5 : 6}
            container
            justifyContent="flex-start"
          >
            <Typography className="PMheader">
              {PMheader}
              <br />
              <Typography className="PMsubtitle">{PMsubtitle}</Typography>
            </Typography>
          </Grid>
          {isEditable && (
            <Grid
              item
              xs={size.height > size.width ? 7 : 6}
              container
              justifyContent="flex-end"
              alignItems="center"
            >
              <Grid item className={classes.pmChecklistSaveButtonGrid}>
                <Button
                  data-testid="pmchecklist-save-button"
                  noMinHeight
                  color="primary"
                  variant="outlined"
                  className={classes.saveButton}
                  onClick={saveData}
                  isLoading={isSaving}
                  disabled={!SavePMChecklist.isChangedTrue}
                >
                  Save
                </Button>
              </Grid>
              <Grid item className={classes.addDeficiencyButtonGrid}>
                <Button
                  noMinHeight
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    setAddFlag(true);
                    setEditDeficiencyDialog(true);
                  }}
                >
                  <Icons.PlusFeather />
                  <Typography className="submitInspectionText">Add Deficiency</Typography>
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Card>
      <Card elevation={1} className={classes.card}>
        {PMChecklistPayload?.length > 0 &&
          pmCheckListLookUpData?.map((mainStep, index) => (
            <>
              {mainStep.children?.length > 0 ? (
                <div>
                  <Grid xs={12} className={classes.childrenContainer}>
                    <Grid xs={12} container className={classes.grids}>
                      <Grid item xs={1} className={classes.stepGrid} direction="row">
                        <Typography className={size.height > size.width ? 'stepVertical' : 'step'}>
                          {`Step ${mainStep.stepNo}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={11}>
                        <Typography>
                          <pre className={classes.preTag}>{mainStep.stepDetails.trim()}</pre>
                          <br />
                        </Typography>
                      </Grid>
                    </Grid>
                    {mainStep?.children?.map((subStep, childIndex) => (
                      <CheckListUI
                        stepDetails={subStep.stepDetails}
                        stepNo={subStep.stepNo}
                        assetCategoryInspectionTypeCheckListId={
                          subStep.assetCategoryInspectionTypeCheckListId
                        }
                        index={childIndex}
                        isChild={true}
                      />
                    ))}
                  </Grid>
                </div>
              ) : (
                <CheckListUI
                  stepDetails={mainStep.stepDetails}
                  stepNo={mainStep.stepNo}
                  assetCategoryInspectionTypeCheckListId={
                    mainStep.assetCategoryInspectionTypeCheckListId
                  }
                  index={index}
                  isChild={false}
                />
              )}
            </>
          ))}
        {editDeficiencyDialog && (
          <EditDeficiency
            open={editDeficiencyDialog}
            addFlag={addFlag}
            onClose={() => {
              setAddFlag(false);
              setEditFile(undefined);
              setEditDeficiencyDialog(false);
            }}
            editFile={editFile as Deficiency}
            taskData={taskData}
            isEditable={isEditable}
          />
        )}
      </Card>
    </div>
  );
};

export default observer(PMChecklist);
